import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { createEBTCard, deleteEBTCard } from 'api/jalapeno';
import { SUBSCRIPTION_QUERY_KEY } from 'api/queries/useSubscriptionQuery';

const API_MESSAGES = {
    SUCCESS: 'Success',
    UNKNOWN_ERROR: 'Unknown server error',
};

/**
 * Hook for managing EBT card operations (save and delete)
 * @param {string} subID - Subscription ID
 * @returns {Object} Card operations and state
 */
export const useEBTCardOperations = (subID) => {
    const queryClient = useQueryClient();

    // Loading states
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Error states
    const [saveError, setSaveError] = useState(null);
    const [deleteError, setDeleteError] = useState(null);

    /**
     * Clears all errors and resets loading states
     */
    const clearErrors = useCallback(() => {
        setSaveError(null);
        setDeleteError(null);
        setIsSaving(false);
        setIsDeleting(false);
    }, []);

    /**
     * Deletes the EBT card for the subscription
     * @returns {Promise<Object>} API response
     * @throws {Error} When subID is missing or API call fails
     */
    const deleteCard = useCallback(async () => {
        if (!subID) {
            throw new Error('Missing subscription ID');
        }

        setIsDeleting(true);
        setDeleteError(null);

        try {
            const response = await deleteEBTCard(subID);
            if (response?.msg !== API_MESSAGES.SUCCESS) {
                throw new Error(response?.msg || API_MESSAGES.UNKNOWN_ERROR);
            }

            await queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_QUERY_KEY] });

            return response;
        } catch (error) {
            setDeleteError(error);
            throw error;
        } finally {
            setIsDeleting(false);
        }
    }, [subID, queryClient]);

    /**
     * Saves EBT card with tokenization
     * @param {Object} forage - Forage instance for tokenization
     * @param {Object} cardElement - Card element from payment form
     * @returns {Promise<Object>} API response
     * @throws {Error} When required params are missing or API call fails
     */
    const saveCardWithTokenization = useCallback(
        async (forage, cardElement) => {
            if (!forage || !cardElement || !subID) {
                throw new Error('Missing required data for saving card');
            }

            setIsSaving(true);
            setSaveError(null);

            try {
                // Tokenization operation
                const tokenizationResult = await forage.tokenizeCard(cardElement);
                const { card, ref: paymentMethodRef } = tokenizationResult;

                // API save operation
                const response = await createEBTCard(subID, {
                    ref: paymentMethodRef,
                    state: card.state,
                });

                if (response?.msg !== API_MESSAGES.SUCCESS) {
                    throw new Error(response?.msg || API_MESSAGES.UNKNOWN_ERROR);
                }

                await queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_QUERY_KEY] });

                return response;
            } catch (error) {
                setSaveError(error);
                throw error;
            } finally {
                setIsSaving(false);
            }
        },
        [subID, queryClient]
    );

    return {
        deleteCard,
        saveCardWithTokenization,
        clearErrors,
        isSaving,
        isDeleting,
        saveError,
        deleteError,
    };
};
